import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import { arrayMove, rectSortingStrategy, SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { disableArrowKeys, fmtLocalTime } from "components/dashboard/data-input-output-table/utils";
import {
  HighlightColor,
  HighlightRange,
  MultiValueProps,
  SelectionValuesType,
} from "components/dashboard/product-template-table/utils/interfaces";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { DropdownChangeEvent } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import React, { useEffect, useRef, useState } from "react";
import { T } from "services/lang/LanguageService";
import { CellSubType } from "utils/enums";
import { convertTimeSpanToString, TimeSpan, timeSpanToSeconds } from "utils/time-utils";
import { v4 as uuidv4 } from "uuid";
import Dataset from "../dataset/Dataset";
import ChipsEditor from "./ChipsEditor";
import EditableCell from "./EditableCell";
import "./MultiValue.scss";

interface IMultiValueProps {
  selectionValues?: SelectionValuesType;
  changeHandler: (value: any) => void;
  subType?: CellSubType;
  values: MultiValueProps | null;
  isInEditMode: boolean;
  maxAge?: number;
  updateFrequency?: number;
  highlightColor?: HighlightColor | null;
  bgHighlightRanges?: HighlightRange[] | null;
  inlineDisplay?: boolean;
  onHighlightColors?: (color?: string, bgColor?: string, colorEntireRow?: boolean) => void;
}

export type SelectionValueTypeAlias = string | number | TimeSpan;
type DropdownOptionType = { label: string; value: SelectionValueTypeAlias };

const normalizeTimespan = (timespan: TimeSpan): TimeSpan => {
  return {
    days: timespan.days || 0,
    hours: timespan.hours || 0,
    minutes: timespan.minutes || 0,
    seconds: timespan.seconds || 0,
  };
};

const sortValues = (a: SelectionValueTypeAlias, b: SelectionValueTypeAlias, subType: CellSubType): number => {
  switch (subType) {
    case CellSubType.STRING:
      return (a as string).localeCompare(b as string);
    case CellSubType.LONG:
    case CellSubType.DOUBLE:
      return (a as number) - (b as number);
    case CellSubType.TIMESPAN:
      return timeSpanToSeconds(a as TimeSpan) - timeSpanToSeconds(b as TimeSpan);
    case CellSubType.TIME:
      return new Date(a as string).getTime() - new Date(b as string).getTime();
    default:
      return 0;
  }
};

const formatValue = (value: SelectionValueTypeAlias, subType: CellSubType): string => {
  switch (subType) {
    case CellSubType.STRING:
    case CellSubType.LONG:
    case CellSubType.DOUBLE:
      return value.toString();
    case CellSubType.TIMESPAN:
      return convertTimeSpanToString(value as TimeSpan);
    case CellSubType.TIME:
      return fmtLocalTime(value as string);
    default:
      return "";
  }
};

const generateDropdownOptions = (
  selectionValues: SelectionValuesType | undefined,
  subType: CellSubType
): DropdownOptionType[] => {
  if (!selectionValues) {
    return [];
  }

  const options = selectionValues.map(value => {
    return { label: formatValue(value, subType), value };
  });

  options.sort((a, b) => sortValues(a.value, b.value, subType));

  return options;
};

const generateValue = (value: SelectionValueTypeAlias, subType: CellSubType): string => {
  return formatValue(value, subType);
};

const renderValues = (values: SelectionValueTypeAlias[], subType: CellSubType) => {
  return values.map((value: SelectionValueTypeAlias, index: number) => {
    return (
      // eslint-disable-next-line
      <span key={index} className="white-space-normal">
        {generateValue(value, subType)}
      </span>
    );
  });
};

export default function MultiValue(props: IMultiValueProps) {
  const selectionValuesSubType = props.subType ?? CellSubType.STRING;
  const options = generateDropdownOptions(props.selectionValues, selectionValuesSubType);

  const selectedItems = props.values
    ? props.values.values
        .map((value: any) => {
          let option;
          switch (props.subType) {
            case CellSubType.STRING:
            case CellSubType.LONG:
            case CellSubType.DOUBLE:
            case CellSubType.TIME:
              option = options.find(o => o.value === value.toString());
              break;
            case CellSubType.TIMESPAN:
              option = options.find(
                o =>
                  JSON.stringify(normalizeTimespan(o.value as TimeSpan)) ===
                  JSON.stringify(normalizeTimespan(value))
              );
              break;
            default:
              break;
          }
          return option ? option.value : null;
        })
        .filter((item: any) => item !== null)
    : [];

  const handleMultiSelect = () => {
    if (props.subType === CellSubType.DATASET) {
      return (
        <Dataset
          values={props.values?.values.map(v => String(v)) ?? []}
          changeHandlerMulti={(newValues: string[] | null) =>
            props.changeHandler({
              subType: selectionValuesSubType,
              values: newValues,
            })
          }
          isInEditMode
          maxAge={props.maxAge}
          updateFrequency={props.updateFrequency}
          onHighlightColors={props.onHighlightColors}
        />
      );
    }

    if (
      props.subType === CellSubType.STRING ||
      props.subType === CellSubType.LONG ||
      props.subType === CellSubType.DOUBLE ||
      props.subType === CellSubType.TIME
    ) {
      return (
        <EditableChips
          values={props.values?.values.map(v => String(v)) ?? []}
          subType={selectionValuesSubType as CellSubType}
          onChange={(newValues: string[]) =>
            props.changeHandler({
              subType: selectionValuesSubType,
              values: newValues,
            })
          }
          selectionValues={props.selectionValues}
        />
      );
    }

    return (
      <MultiSelect
        value={selectedItems}
        options={options}
        placeholder={T("action.select")}
        onChange={e =>
          props.changeHandler({
            subType: selectionValuesSubType,
            values: e.value,
          })
        }
        className="w-full"
        filter
        emptyFilterMessage={T("message.no-results-found")}
      />
    );
  };

  const valuesDisplayer = () => {
    if (props.subType === CellSubType.DATASET) {
      return (
        <Dataset
          values={props.values?.values.map(v => String(v)) ?? []}
          changeHandlerMulti={(newValues: string[] | null) =>
            props.changeHandler({
              subType: selectionValuesSubType,
              values: newValues,
            })
          }
          isInEditMode={false}
          maxAge={props.maxAge}
          updateFrequency={props.updateFrequency}
          bgHighlightRanges={props.bgHighlightRanges}
          highlightColor={props.highlightColor}
          onHighlightColors={props.onHighlightColors}
        />
      );
    }

    return (
      <div
        className={`flex flex-${props.inlineDisplay ? "row justify-content-around" : "column"} multi-value-container`}
      >
        {props.values?.values.length && props.values?.values.length > 0
          ? renderValues(props.values?.values.map(v => v) ?? [], selectionValuesSubType)
          : "-"}
      </div>
    );
  };

  return props.isInEditMode ? handleMultiSelect() : valuesDisplayer();
}

type ChipItem = {
  id: string;
  value: string;
};

const EditableChips = ({
  values,
  subType,
  selectionValues,
  onChange,
}: {
  values: string[];
  subType: CellSubType;
  selectionValues?: SelectionValuesType;
  onChange: (v: string[]) => void;
}) => {
  const [inputValue, setInputValue] = useState("");
  const [isChipsEditorVisible, setIsChipsEditorVisible] = useState(false);
  const [items, setItems] = useState<ChipItem[]>(() => values.map(value => ({ id: uuidv4(), value })));

  const internalUpdateRef = useRef(false);
  const pendingUpdateRef = useRef<ChipItem[] | null>(null);

  useEffect(() => {
    if (internalUpdateRef.current) {
      internalUpdateRef.current = false;
      return;
    }
    setItems(values.map(value => ({ id: uuidv4(), value })));
  }, [values]);

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));

  const emitChange = (next: ChipItem[]) => {
    internalUpdateRef.current = true;
    setItems(next);
    onChange(next.map(i => i.value));
  };

  const handleEditorChange = (updater: (prev: ChipItem[]) => ChipItem[]) => {
    setItems(prev => {
      const next = updater(prev);
      pendingUpdateRef.current = next;
      return next;
    });
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex(i => i.id === active.id);
    const newIndex = items.findIndex(i => i.id === over.id);

    emitChange(arrayMove(items, oldIndex, newIndex));
  };

  const handleDelete = (index: number) => {
    emitChange(items.filter((_, i) => i !== index));
  };

  const handleSortableChipEdit = (index: number, newValue: string) => {
    const next = [...items];
    next[index] = { ...next[index], value: newValue };
    emitChange(next);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    disableArrowKeys(e);

    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();

      const trimmed = inputValue.trim();
      if (!trimmed) return;

      emitChange([...items, { id: uuidv4(), value: trimmed }]);
      setInputValue("");
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement> | DropdownChangeEvent) => {
    const { value } = e.target;
    if (subType === CellSubType.LONG) {
      if (/^-?\d*$/.test(value)) setInputValue(value);
    } else {
      setInputValue(value);
    }
  };

  const handleCloseDialog = () => {
    emitChange(items);
    setIsChipsEditorVisible(false);
  };

  const dialogFooterTemplate = () => (
    <div className="flex justify-content-end">
      <Button
        label={T("word.done")}
        icon="pi pi-check"
        onClick={handleCloseDialog}
        className="component-editor-button icon-green"
      />
    </div>
  );

  return (
    <div className="flex flex-column gap-2 justify-content-between multi-value-chips-container">
      <div className="hidden sm:block">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToParentElement]}
        >
          <SortableContext items={items.map(i => i.id)} strategy={rectSortingStrategy}>
            <div className="droppable-chips-container">
              {items.map((item, index) => (
                <SortableChip
                  key={item.id}
                  id={item.id}
                  chip={item.value}
                  index={index}
                  subType={subType}
                  handleDelete={handleDelete}
                  handleEdit={handleSortableChipEdit}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      <div className="flex flex-row gap-1 justify-content-center">
        {!selectionValues && (
          <input
            type={subType === CellSubType.STRING ? "text" : "number"}
            value={inputValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={T("word.value")}
            className="chips-input-field hidden sm:block"
          />
        )}

        <Button
          icon="pi pi-list"
          className="bg-gray-200 border-none text-black-alpha-90 hover:bg-gray-300 h-2rem"
          onClick={() => setIsChipsEditorVisible(true)}
        />
      </div>

      {isChipsEditorVisible && (
        <Dialog
          visible
          onHide={handleCloseDialog}
          header={T("word.values")}
          style={{ width: "25rem" }}
          footer={dialogFooterTemplate}
          onClick={e => e.stopPropagation()}
        >
          <ChipsEditor
            items={items}
            subType={subType}
            selectionValues={selectionValues}
            onChange={handleEditorChange}
          />
        </Dialog>
      )}
    </div>
  );
};

const SortableChip = ({
  id,
  chip,
  index,
  handleDelete,
  subType,
  handleEdit,
}: {
  id: string;
  chip: string;
  index: number;
  handleDelete: (index: number) => void;
  subType: CellSubType;
  handleEdit: (i: number, newValue: string) => void;
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleDoubleClick = () => {
    setIsEditing(true);
  };

  return (
    <div ref={setNodeRef} style={style} className="single-chip">
      {isEditing ? (
        <EditableCell
          value={chip}
          subType={subType}
          onCommit={newValue => handleEdit(index, newValue)}
          hideInputStyles
        />
      ) : (
        <span {...attributes} {...listeners} className="pr-3" onDoubleClick={handleDoubleClick}>
          {chip}
        </span>
      )}
      <Button
        icon="pi pi-times"
        className="p-button-text p-button-sm py-1 pr-1 pl-0 w-full"
        onClick={() => handleDelete(index)}
      />
    </div>
  );
};
